import { Box, Paper, Typography } from '@mui/material';
import React from 'react';
import { AdvanceRequestItem, requestRefundItem } from './advance.helper';
import { useTranslation } from 'react-i18next';


const SummaryCard: React.FC<{ summary: AdvanceRequestItem | requestRefundItem | null }> = ({ summary }) => {
  const cardSx = {
    borderRadius: 4,
    bgcolor: '#ffffff',
    boxShadow: 'none',
    border: '1px solid transparent',
    overflow: 'hidden',
  } as const;

  const labelSx = { color: 'text.secondary', fontSize: 13, mb: 0.5 } as const;
  const valueSx = { fontWeight: 700, fontSize: 15 } as const;
  const { t : tBilling } = useTranslation('billing_config');

  const amountColor = (key: string, val: number | string | undefined): string => {
    const n = typeof val === 'number' ? val : parseFloat(String(val ?? '0').replace(/,/g, ''));
    switch (key) {
    case 'total_amount':
    case 'package_net_amount':
      return '#805ad5'; // purple
    case 'used_amount':
    case 'advance_paid':
    case 'refund_amount':
      return '#38a169'; // green
    case 'balance_amt':
    case 'package_balance_amount':
      return n < 0 ? '#c53030' : '#dd6b20'; // orange or red
    default:
      return 'text.primary';
    }
  };

  const to2 = (v: number | string | undefined): string => {
    if (v === undefined || v === null) return '0.00';
    const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, ''));
    if (isNaN(n)) return String(v);
    return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Type guards
  const isRefund = (obj: unknown): obj is requestRefundItem => {
    return obj !== null && obj !== undefined && typeof obj === 'object' && 'receipt_no' in obj && 'transaction_date' in obj && 'total_amount' in obj && 'used_amount' in obj && 'balance_amount' in obj && 'refund_amount' in obj && 'advance_against' in obj;
  };

  return (
    <Paper elevation={0} variant="outlined" sx={{ ...cardSx, border: '1px dashed #e2e8f0', borderRadius: 2 }} data-testid="package-summary-card">
      <Box sx={{ backgroundColor: '#f7fafc', px: 1.5, py: 1, borderBottom: '1px solid #e5e7eb', borderRadius: '8px 8px 0 0' }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#374151' }}>
          {tBilling('package_details.package_details')}
        </Typography>
      </Box>

      <Box sx={{ px: 1.5, py: 1 }}>
        {/* reuse spacing/row styles from consumption card */}
        {isRefund(summary) ? (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1.5, mb: 1, pb: 1, borderBottom: '1px dashed #e2e8f0' }}>
              <Box>
                <Typography variant="caption" sx={labelSx}>{tBilling('Advance.package_name')}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a365d', fontSize: '0.8rem' }}>
                  {summary?.package_name ?? '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={labelSx}>{tBilling('Advance.receipt_no')}</Typography>
                <Typography variant="body2" sx={{ ...valueSx, color: amountColor('receipt_no', 0) }}>{summary?.receipt_no ?? '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={labelSx}>{tBilling('Advance.date')}</Typography>
                <Typography variant="body2" sx={{ ...valueSx, color: amountColor('date', 0) }}>{summary?.transaction_date ? summary.transaction_date.split('T')[0].split('-').reverse().join('/') : '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={labelSx}>{tBilling('package_details.total_amount')}</Typography>
                <Typography variant="body2" sx={{ ...valueSx, color: amountColor('total_amount', summary?.total_amount) }}>{to2(summary?.total_amount ?? 0)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={labelSx}>{tBilling('Advance.used_amount')}</Typography>
                <Typography variant="body2" sx={{ ...valueSx, color: amountColor('used_amount', summary?.used_amount) }}>{to2(summary?.used_amount ?? 0)}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1.5}}>
              <Box>
                <Typography variant="caption" sx={labelSx}>{tBilling('Advance.balance_amt')}</Typography>
                <Typography variant="body2" sx={{ ...valueSx, color: amountColor('balance_amt', summary?.balance_amount) }}>{to2(summary?.balance_amount ?? 0)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={labelSx}>{tBilling('Advance.refund_amount')}</Typography>
                <Typography variant="body2" sx={{ ...valueSx, color: amountColor('refund_amount', summary?.refund_amount) }}>{to2(summary?.refund_amount ?? 0)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={labelSx}>{tBilling('Advance.against')}</Typography>
                <Typography variant="body2" sx={valueSx}>{summary?.advance_against ?? '-'}</Typography>
              </Box>
              <Box />
              <Box />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1.5 }}>
              <Box />
              <Box />
              <Box />
              <Box />
              <Box />
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1.5, mb: 1, pb: 1, borderBottom: '1px dashed #e2e8f0' }}>
              <Box>
                <Typography variant="caption" sx={labelSx}>{tBilling('Advance.package_name')}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a365d', fontSize: '0.8rem' }}>
                  {summary?.package_name ?? '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={labelSx}>{tBilling('Advance.package_net_amount')}</Typography>
                <Typography variant="body2" sx={{ ...valueSx, color: amountColor('package_net_amount', summary?.package_net_amount ?? 0) }}>{to2(summary?.package_net_amount ?? 0)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={labelSx}>{tBilling('Advance.package_paid_amount')}</Typography>
                <Typography variant="body2" sx={{ ...valueSx, color: amountColor('advance_paid', summary?.package_paid_amount ?? 0) }}>{to2(summary?.package_paid_amount ?? 0)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={labelSx}>{tBilling('Advance.package_balance_amount')}</Typography>
                <Typography variant="body2" sx={{ ...valueSx, color: amountColor('package_balance_amount', summary?.package_net_amount && summary?.package_paid_amount ? summary?.package_net_amount - summary?.package_paid_amount : 0) }}>{to2(summary?.package_net_amount && summary?.package_paid_amount ? summary?.package_net_amount - summary?.package_paid_amount : 0)}</Typography>
              </Box>
              <Box />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1.5, mb: 1, pb: 1, borderBottom: '1px dashed #e2e8f0' }}>
              <Box />
              <Box />
              <Box />
              <Box />
              <Box />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1.5 }}>
              <Box />
              <Box />
              <Box />
              <Box />
              <Box />
            </Box>
          </>
        )}
      </Box>
    </Paper>
  );
};

export default SummaryCard;